/** A comment already on a document — a remark that opened a thread, or a reply inside one. */
export interface DocComment {
  id: string;
  rev: number;
  ts: number;
  status: string;
  author: string;
  quote: string;
  text: string;
  /** Set on a reply: the comment that opened its thread. */
  parent?: string;
}

/** A remark that opened a thread, and the replies under it in the order they were written. */
export interface Thread {
  root: DocComment;
  replies: DocComment[];
}

/** A comment written in this pass and not yet submitted. `parent` makes it a reply to that thread. */
export interface PendingComment {
  id: string;
  quote: string;
  text: string;
  parent?: string;
}

/** groupThreads arranges comments into threads, oldest first. A reply whose thread is gone stands as its own. */
export function groupThreads(comments: DocComment[]): Thread[] {
  const sorted = [...comments].sort((a, b) => a.ts - b.ts);
  const roots = new Map<string, Thread>();
  for (const c of sorted) if (!c.parent) roots.set(c.id, { root: c, replies: [] });
  for (const c of sorted) {
    if (!c.parent) continue;
    const thread = roots.get(c.parent);
    if (thread) thread.replies.push(c);
    else roots.set(c.id, { root: c, replies: [] });
  }
  return [...roots.values()].sort((a, b) => a.root.ts - b.root.ts);
}

/** reviewSummary is the chat line announcing a submitted round; the remarks themselves are read from their rows. */
export function reviewSummary(comments: number, replies: number, doc: { name: string; rev: number }): string {
  const parts = [
    comments ? `${comments} comment${comments === 1 ? "" : "s"}` : "",
    replies ? `${replies} repl${replies === 1 ? "y" : "ies"}` : "",
  ].filter(Boolean);
  return `Left ${parts.join(" and ")} on **${doc.name}** (v${doc.rev}).`;
}

const draftKey =(docId: string) => `failover.review.${docId}`;

/** loadReviewDrafts reads the unsubmitted comments kept for one revision; none when storage is unavailable. */
export function loadReviewDrafts(docId: string): PendingComment[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(draftKey(docId)) ?? "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (p): p is PendingComment => !!p && typeof p.id === "string" && typeof p.quote === "string" && typeof p.text === "string",
    );
  } catch {
    return [];
  }
}

/** saveReviewDrafts keeps one revision's unsubmitted comments across a closed reader or a reload. */
export function saveReviewDrafts(docId: string, drafts: PendingComment[]): void {
  try {
    if (drafts.length) localStorage.setItem(draftKey(docId), JSON.stringify(drafts));
    else localStorage.removeItem(draftKey(docId));
  } catch {
    // Storage blocked: the round still works for as long as the reader stays open.
  }
}
